import axios, { AxiosInstance, CancelTokenSource } from 'axios';

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // Base delay in ms
const RETRY_STATUS_CODES = [408, 429, 500, 502, 503, 504];

// Logging levels
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

// Logger class for debugging
export class ApiLogger {
  private enabled: boolean;
  private level: LogLevel;

  constructor(enabled = true, level = LogLevel.INFO) {
    this.enabled = enabled && process.env.NODE_ENV === 'development';
    this.level = level;
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.enabled) return false;
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    return levels.indexOf(level) >= levels.indexOf(this.level);
  }

  debug(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(`[API DEBUG] ${message}`, data || '');
    }
  }

  info(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(`[API INFO] ${message}`, data || '');
    }
  }

  warn(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`[API WARN] ${message}`, data || '');
    }
  }

  error(message: string, error?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`[API ERROR] ${message}`, error || '');
    }
  }
}

// Request cancellation manager
export class CancellationManager {
  private requests: Map<string, CancelTokenSource> = new Map();

  create(key: string): CancelTokenSource {
    // Cancel existing request with same key if exists
    this.cancel(key);
    
    const source = axios.CancelToken.source();
    this.requests.set(key, source);
    return source;
  }

  cancel(key: string, reason = 'Request cancelled'): void {
    const source = this.requests.get(key);
    if (source) {
      source.cancel(reason);
      this.requests.delete(key);
    }
  }

  cancelAll(reason = 'All requests cancelled'): void {
    this.requests.forEach(source => source.cancel(reason));
    this.requests.clear();
  }

  getToken(key: string): CancelTokenSource | undefined {
    return this.requests.get(key);
  }
}


// Enhanced API client factory
export function createApiClient(
  baseURL: string,
  token?: string,
  options?: {
    timeout?: number;
    enableRetry?: boolean;
    enableLogging?: boolean;
    logLevel?: LogLevel;
  }
): {
  client: AxiosInstance;
  logger: ApiLogger;
  cancellation: CancellationManager;
} {
  const {
    timeout = 30000,
    enableRetry = true,
    enableLogging = true,
    logLevel = LogLevel.INFO
  } = options || {};

  const logger = new ApiLogger(enableLogging, logLevel);
  const cancellation = new CancellationManager();

  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    }
  });

  // Request interceptor for logging and token.
  //
  // DAG-006 hardening: we used to log `config.params` and `config.data`,
  // which on these routes can contain CV text, generated bios, scraped
  // client content, and manual project context. Browser-console logs
  // bleed into screenshots, remote debugging sessions, and bug reports,
  // so payload bodies are stripped here. Method/url/status/timing stay.
  client.interceptors.request.use(
    (config) => {
      logger.debug(`Request: ${config.method?.toUpperCase()} ${config.url}`);

      // Update token if provided
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      logger.error('Request interceptor error', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor for logging and error handling
  client.interceptors.response.use(
    (response) => {
      // DAG-006: response.data omitted from the log to avoid teeing
      // governed content into browser console / remote debugging.
      logger.debug(`Response: ${response.status} ${response.config.url}`);
      return response;
    },
    async (error) => {
      if (axios.isCancel(error)) {
        logger.info('Request cancelled', error.message);
        return Promise.reject(error);
      }

      // DAG-006: error.response.data on a 4xx/5xx commonly echoes
      // the request payload — keep status + axios error message only.
      logger.error(`Response error: ${error.config?.url}`, {
        status: error.response?.status,
        message: error.message
      });

      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        logger.warn('Unauthorized access, redirecting to login');
        // You might want to emit an event here instead
        // eventBus.emit('auth:expired');
        window.location.href = '/login';
      }

      // Retry logic
      if (enableRetry && error.config && !error.config.__isRetryRequest) {
        error.config.__isRetryRequest = true;
        error.config.__retryCount = (error.config.__retryCount || 0) + 1;
        
        if (error.config.__retryCount <= MAX_RETRIES) {
          const shouldRetry = 
            error.code === 'ECONNABORTED' ||
            error.code === 'ETIMEDOUT' ||
            error.code === 'ECONNRESET' ||
            (!error.response && error.request) ||
            (error.response && RETRY_STATUS_CODES.includes(error.response.status));

          if (shouldRetry) {
            const delay = RETRY_DELAY * Math.pow(2, error.config.__retryCount - 1);
            logger.info(`Retrying request (${error.config.__retryCount}/${MAX_RETRIES}) after ${delay}ms`);
            
            await new Promise(resolve => setTimeout(resolve, delay));
            return client.request(error.config);
          }
        }
      }

      return Promise.reject(error);
    }
  );

  return { client, logger, cancellation };
}

// Helper function to create a request with cancellation
export function createCancellableRequest<T>(
  requestFn: (cancelToken: any) => Promise<T>,
  cancellation: CancellationManager,
  key: string
): { promise: Promise<T>; cancel: () => void } {
  const source = cancellation.create(key);
  
  const promise = requestFn(source.token);
  
  return {
    promise,
    cancel: () => cancellation.cancel(key)
  };
}

// Export a default instance for backward compatibility
const defaultApiConfig = createApiClient(
  process.env.REACT_APP_API_URL || 'http://localhost:9110',
  undefined,
  {
    enableRetry: true,
    enableLogging: process.env.NODE_ENV === 'development',
    logLevel: LogLevel.INFO
  }
);

export const apiClient = defaultApiConfig.client;
export const apiLogger = defaultApiConfig.logger;
export const apiCancellation = defaultApiConfig.cancellation;